import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { addDoc, collection, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuCalendarDays, LuClipboardList, LuHouse, LuLink, LuLoaderCircle, LuNotebookPen, LuPlus, LuSave, LuUserPlus, LuUserPen, LuX } from 'react-icons/lu';
// Only global theme variables are left in this file; importing it keeps their place in the cascade
import './AddPatientModal.module.css';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { Field, SelectInput, TextArea, TextInput, ToggleTile } from '../../components/form/FormControls';
import { db } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useTranslation } from '../../i18n/LanguageContext';
import { claimPatient, findProjectPatientsByStudyId } from '../../utils/patientAccess';
import { ENROLLMENT_REQUIRED, PATIENT_STATUSES, statusOf } from './patients/patientStatuses';

const FORM_ID = 'patient-form';

const toForm = (patient) => ({
  patientId: patient?.patientId || '',
  status: patient?.status || 'planned',
  enrollmentDate: patient?.enrollmentDate || '',
  firstDoseDate: patient?.firstDoseDate || '',
  currentVisitNumber: patient?.currentVisitNumber || '',
  hasHomeVisit: patient?.hasHomeVisit || false,
  homeVisitDates: patient?.homeVisitDates || [],
  homeVisitInfo: patient?.homeVisitInfo || '',
  notes: patient?.notes || '',
});

const Section = ({ icon: Icon, title, children }) => (
  <section className="tw-space-y-3">
    <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">
      <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
      {title}
    </h3>
    {children}
  </section>
);

Section.propTypes = { icon: PropTypes.elementType.isRequired, title: PropTypes.string.isRequired, children: PropTypes.node };

/**
 * "Yeni Hasta Ekle" / "Hasta Bilgilerini Düzenle" (`patient` given) for a
 * project; also opened from the personal calendar. A Hasta ID already
 * registered in the project is offered instead of a duplicate: using it only
 * adds the user to its owners. `onSave(patient)` gets the saved record.
 */
const AddPatientModal = ({ project, patient, onClose, onSave }) => {
  const { t, language, locale } = useTranslation();
  const { employeeId } = useUserProfile();
  const isEditing = Boolean(patient);
  const initial = useMemo(() => toForm(patient), [patient]);
  const [formData, setFormData] = useState(initial);
  const [newHomeVisitDate, setNewHomeVisitDate] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  // Bu Hasta ID ile projede zaten kayıtlı hasta (kullanıcıya sorulurken)
  const [existingPatient, setExistingPatient] = useState(null);
  const [linking, setLinking] = useState(false);
  const label = (key, vars) => t(`projectManagement.addPatient.${key}`, vars);

  const setValue = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };
  const handleChange = (event) => setValue(event.target.name, event.target.value);

  const handleAddHomeVisitDate = () => {
    if (!newHomeVisitDate) {
      toast.warning(label('toast.chooseDate'));
      return;
    }
    if (formData.homeVisitDates.includes(newHomeVisitDate)) {
      toast.warning(label('toast.dateExists'));
      return;
    }
    setFormData((prev) => ({ ...prev, homeVisitDates: [...prev.homeVisitDates, newHomeVisitDate].sort() }));
    setNewHomeVisitDate('');
  };

  const handleRemoveHomeVisitDate = (dateToRemove) => {
    setFormData((prev) => ({ ...prev, homeVisitDates: prev.homeVisitDates.filter((date) => date !== dateToRemove) }));
  };

  // Bu projede aynı Hasta ID ile kayıtlı hastalar (düzenlenen hasta hariç)
  const findExistingPatients = async (patientId) => {
    try {
      const matches = await findProjectPatientsByStudyId(project.id, patientId);
      return isEditing ? matches.filter((match) => match.id !== patient.id) : matches;
    } catch (error) {
      console.error('Error checking duplicate patient ID:', error);
      return [];
    }
  };

  // "Mevcut hastayı kullan": kayıt değişmez, kullanıcı yalnızca sahiplerine eklenir
  const handleUseExistingPatient = async () => {
    if (!existingPatient) return;
    setLinking(true);
    try {
      await claimPatient(existingPatient.id, employeeId);
      const linkedPatient = {
        ...existingPatient,
        ownerIds: Array.from(new Set([...(existingPatient.ownerIds || []), employeeId].filter(Boolean))),
      };
      toast.success(label('toast.linked'));
      setExistingPatient(null);
      onSave(linkedPatient);
    } catch (error) {
      console.error('Error linking existing patient:', error);
      toast.error(label('toast.linkError'));
    } finally {
      setLinking(false);
    }
  };

  const validateForm = () => {
    const found = {};
    if (!formData.patientId.trim()) found.patientId = label('errors.patientId');
    if (!formData.status) found.status = label('errors.status');
    if (ENROLLMENT_REQUIRED.includes(formData.status) && !formData.enrollmentDate) found.enrollmentDate = label('errors.enrollmentDate');
    if (formData.status === 'onMedication' && !formData.firstDoseDate) found.firstDoseDate = label('errors.firstDoseDate');
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      toast.error(label('toast.fixErrors'));
      return;
    }
    setSaving(true);
    try {
      const matches = await findExistingPatients(formData.patientId);
      if (matches.length > 0) {
        if (isEditing) {
          // Başka bir hastanın kimliğine çevirmek gerçek bir çakışma
          setErrors((prev) => ({ ...prev, patientId: label('errors.inUse') }));
          toast.error(label('toast.duplicate'));
          setSaving(false);
          return;
        }
        // Aynı kişi için ikinci kayıt yerine mevcut kaydı öner
        setExistingPatient(matches[0]);
        setSaving(false);
        return;
      }

      const patientData = {
        projectId: project.id,
        patientId: formData.patientId.trim(),
        status: formData.status,
        enrollmentDate: formData.enrollmentDate || null,
        firstDoseDate: formData.firstDoseDate || null,
        currentVisitNumber: formData.currentVisitNumber ? parseInt(formData.currentVisitNumber, 10) : null,
        hasHomeVisit: formData.hasHomeVisit,
        homeVisitDates: formData.homeVisitDates,
        homeVisitInfo: formData.homeVisitInfo.trim(),
        notes: formData.notes.trim(),
        updatedAt: serverTimestamp(),
      };

      let savedPatient;
      if (isEditing) {
        // ownerIds dokunulmaz; her sahip hastayı görmeye devam eder
        await updateDoc(doc(db, 'Patients', patient.id), patientData);
        savedPatient = { ...patient, ...patientData };
        toast.success(label('toast.updated'));
      } else {
        // ownerIds: hasta, ilk vizitten önce de oluşturanın listesinde görünür
        patientData.createdAt = serverTimestamp();
        patientData.createdBy = employeeId || null;
        patientData.ownerIds = employeeId ? [employeeId] : [];
        const patientRef = await addDoc(collection(db, 'Patients'), patientData);
        savedPatient = { id: patientRef.id, ...patientData };
        toast.success(label('toast.added'));
      }
      onSave(savedPatient);
    } catch (error) {
      console.error('Error saving patient:', error);
      toast.error(isEditing ? label('toast.updateError') : label('toast.addError'));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? dateString : date.toLocaleDateString(locale);
  };

  const enrollmentRequired = ENROLLMENT_REQUIRED.includes(formData.status);

  return (
    <>
      <Modal
        isOpen
        onClose={() => !saving && onClose()}
        size="lg"
        title={isEditing ? label('editTitle') : label('addTitle')}
        subtitle={project.titleShort || undefined}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30" size="xs">
            {isEditing ? <LuUserPen aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /> : <LuUserPlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
          </GlassIcon>
        )}
        footer={(
          <>
            <Button variant="ghost" onClick={onClose} disabled={saving} className="max-sm:tw-hidden">{t('dataTable.form.cancel')}</Button>
            <Button variant="primary" type="submit" form={FORM_ID} disabled={saving} className="max-sm:tw-flex-1">
              {saving
                ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
                : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
              {saving ? t('dataTable.form.saving') : (isEditing ? label('update') : label('save'))}
            </Button>
          </>
        )}
      >
        <form id={FORM_ID} noValidate onSubmit={handleSubmit} className="tw-space-y-6">
          <Section icon={LuClipboardList} title={label('basics')}>
            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <Field label={label('patientId')} htmlFor="patient-form-id" required error={errors.patientId}>
                <TextInput id="patient-form-id" name="patientId" value={formData.patientId} onChange={handleChange} placeholder={label('patientIdPlaceholder')} error={errors.patientId} autoComplete="off" />
              </Field>
              <Field label={label('status')} htmlFor="patient-form-status" required error={errors.status} hint={statusOf(formData.status) ? t(`projectManagement.patientStatusHint.${formData.status}`) : undefined}>
                <SelectInput
                  id="patient-form-status"
                  name="status"
                  value={formData.status}
                  current={formData.status}
                  onChange={handleChange}
                  error={errors.status}
                  options={PATIENT_STATUSES.map((status) => ({ value: status.value, label: t(`projectManagement.patientStatus.${status.value}`) }))}
                />
              </Field>
            </div>
          </Section>

          <Section icon={LuCalendarDays} title={label('dates')}>
            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-3">
              <Field label={label('enrollmentDate')} htmlFor="patient-form-enrollment" required={enrollmentRequired} error={errors.enrollmentDate}>
                <TextInput id="patient-form-enrollment" type="date" name="enrollmentDate" value={formData.enrollmentDate} onChange={handleChange} error={errors.enrollmentDate} />
              </Field>
              <Field
                label={label('firstDoseDate')}
                htmlFor="patient-form-first-dose"
                error={errors.firstDoseDate}
                badge={formData.status === 'onMedication' ? (
                  <span className="tw-rounded-full tw-bg-amber-500/15 tw-px-1.5 tw-text-[10px] tw-font-semibold tw-text-amber-700 dark:tw-text-amber-300">{label('recommended')}</span>
                ) : null}
              >
                <TextInput id="patient-form-first-dose" type="date" name="firstDoseDate" value={formData.firstDoseDate} onChange={handleChange} error={errors.firstDoseDate} />
              </Field>
              <Field label={label('currentVisitNumber')} htmlFor="patient-form-visit">
                <TextInput id="patient-form-visit" type="number" min="1" inputMode="numeric" name="currentVisitNumber" value={formData.currentVisitNumber} onChange={handleChange} placeholder={label('currentVisitPlaceholder')} />
              </Field>
            </div>
          </Section>

          <Section icon={LuHouse} title={label('homeVisit')}>
            <ToggleTile id="patient-form-home" label={label('homeVisitDone')} checked={Boolean(formData.hasHomeVisit)} onChange={(checked) => setValue('hasHomeVisit', checked)} />
            {formData.hasHomeVisit && (
              <div className="tw-space-y-3 tw-rounded-2xl tw-border tw-border-slate-200 tw-p-3 dark:tw-border-white/10">
                <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-2">
                  <Field label={label('homeVisitDate')} htmlFor="patient-form-home-date" className="tw-min-w-[12rem] tw-flex-1">
                    <TextInput id="patient-form-home-date" type="date" value={newHomeVisitDate} onChange={(event) => setNewHomeVisitDate(event.target.value)} />
                  </Field>
                  <Button onClick={handleAddHomeVisitDate}>
                    <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
                    {label('addDate')}
                  </Button>
                </div>
                {formData.homeVisitDates.length > 0 && (
                  <div className="tw-space-y-1.5">
                    <p className="tw-px-0.5 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('addedDates')}</p>
                    <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                      {formData.homeVisitDates.map((date) => (
                        <span key={date} className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-teal-500/10 tw-py-1 tw-pl-3 tw-pr-1 tw-text-xs tw-font-semibold tw-text-teal-800 tw-tabular-nums dark:tw-text-teal-200">
                          {formatDate(date)}
                          <button
                            type="button"
                            onClick={() => handleRemoveHomeVisitDate(date)}
                            aria-label={label('removeDate', { date: formatDate(date) })}
                            className="tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded-full tw-bg-transparent tw-transition-colors hover:tw-bg-teal-900/10 dark:hover:tw-bg-white/15"
                          >
                            <LuX aria-hidden="true" className="tw-h-3 tw-w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <Field label={label('homeVisitInfo')} htmlFor="patient-form-home-info">
                  <TextArea id="patient-form-home-info" name="homeVisitInfo" rows={2} value={formData.homeVisitInfo} onChange={handleChange} placeholder={label('homeVisitInfoPlaceholder')} />
                </Field>
              </div>
            )}
          </Section>

          <Section icon={LuNotebookPen} title={label('notes')}>
            <TextArea id="patient-form-notes" name="notes" rows={3} value={formData.notes} onChange={handleChange} placeholder={label('notesPlaceholder')} aria-label={label('notes')} />
          </Section>
        </form>
      </Modal>

      {/* Bu Hasta ID projede zaten kayıtlı: aynı kişi için ikinci kayıt yerine mevcut olanı öner */}
      <Modal
        isOpen={Boolean(existingPatient)}
        onClose={() => !linking && setExistingPatient(null)}
        size="md"
        title={label('existing.title')}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="xs">
            <LuLink aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setExistingPatient(null)} disabled={linking}>{label('existing.cancel')}</Button>
            <Button variant="primary" onClick={handleUseExistingPatient} disabled={linking} className="max-sm:tw-flex-1">
              {linking
                ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
                : <LuLink aria-hidden="true" className="tw-h-4 tw-w-4" />}
              {linking ? label('existing.linking') : label('existing.use')}
            </Button>
          </>
        )}
      >
        <div className="tw-space-y-3">
          <p className="tw-text-[15px] tw-leading-relaxed tw-text-slate-700 dark:tw-text-slate-200">
            {label('existing.body', { id: existingPatient?.patientId || '' })}
          </p>
          <p className="tw-rounded-xl tw-bg-slate-900/[0.03] tw-px-3.5 tw-py-2.5 tw-text-sm tw-text-slate-500 dark:tw-bg-white/[0.04] dark:tw-text-slate-400">{label('existing.note')}</p>
        </div>
      </Modal>
    </>
  );
};

AddPatientModal.propTypes = {
  project: PropTypes.shape({ id: PropTypes.string.isRequired, titleShort: PropTypes.string }).isRequired,
  patient: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default AddPatientModal;
